import * as GC from '@mescius/spread-sheets';

import {
  collectUniqueLoops,
  findLoopsAffectedByCellShift,
  findLoopsIntersectingRows,
  removeLoopById,
} from './loop';
import { translate } from '../utils/translations';

type ConfirmFn = (params: { message: string }) => Promise<boolean>;

type SafeAddRowsCommandOptions = {
  sheetName: string;
  row: number;
  rowCount?: number;
  loopIds: number[];
};

type SafeDeleteRowsCommandOptions = {
  sheetName: string;
  row: number;
  rowCount?: number;
  loopIds: number[];
};

type SafeCellShiftCommandOptions = {
  sheetName: string;
  range: GC.Spread.Sheets.Range;
  loopIds: number[];
};

/**
 * 行の追加・削除を行うカスタムコマンド
 */
function registerSafeRowCommand(
  spread: GC.Spread.Sheets.Workbook,
  commandName: 'safeAddRows' | 'safeDeleteRows',
  applyOperation: (
    sheet: GC.Spread.Sheets.Worksheet,
    options: SafeAddRowsCommandOptions | SafeDeleteRowsCommandOptions
  ) => void
): void {
  const commandManager = spread.commandManager();

  commandManager.register(commandName, {
    canUndo: true,
    execute: (
      context: GC.Spread.Sheets.Workbook,
      options: SafeAddRowsCommandOptions | SafeDeleteRowsCommandOptions,
      isUndo: boolean
    ): boolean => {
      const Commands = GC.Spread.Sheets.Commands;

      if (isUndo) {
        Commands.undoTransaction(context, options);
        return true;
      }

      const sheet = context.getSheetFromName(options.sheetName);
      if (!sheet) {
        return false;
      }

      Commands.startTransaction(context, options);

      try {
        for (const loopId of options.loopIds) {
          removeLoopById(sheet, loopId);
        }

        applyOperation(sheet, options);

        Commands.endTransaction(context, options);
        return true;
      } catch (error) {
        Commands.undoTransaction(context, options);
        throw error;
      }
    },
  });
}

/**
 *  セルの上下方向への追加・削除を行うカスタムコマンド
 */
function registerSafeCellShiftCommand(
  spread: GC.Spread.Sheets.Workbook,
  commandName: 'safeInsertCellsDown' | 'safeDeleteCellsUp',
  baseCommandName: 'Designer.insertDownCells' | 'Designer.deleteUpCells'
): void {
  const commandManager = spread.commandManager();

  commandManager.register(commandName, {
    canUndo: true,
    execute: (
      context: GC.Spread.Sheets.Workbook,
      options: SafeCellShiftCommandOptions,
      isUndo: boolean
    ): boolean => {
      const Commands = GC.Spread.Sheets.Commands;

      if (isUndo) {
        Commands.undoTransaction(context, options);
        return true;
      }

      const sheet = context.getSheetFromName(options.sheetName);
      if (!sheet) {
        return false;
      }

      Commands.startTransaction(context, options);

      try {
        for (const loopId of options.loopIds) {
          removeLoopById(sheet, loopId);
        }

        context.commandManager().execute({
          cmd: baseCommandName,
          sheetName: options.sheetName,
          selections: [options.range],
        });

        Commands.endTransaction(context, options);
        return true;
      } catch (error) {
        Commands.undoTransaction(context, options);
        throw error;
      }
    },
  });
}

function registerSafeAddRowsCommand(spread: GC.Spread.Sheets.Workbook): void {
  registerSafeRowCommand(
    spread,
    'safeAddRows',
    (
      sheet: GC.Spread.Sheets.Worksheet,
      options: SafeAddRowsCommandOptions | SafeDeleteRowsCommandOptions
    ): void => {
      sheet.addRows(options.row, options.rowCount ?? 1);
    }
  );
}

function registerSafeDeleteRowsCommand(
  spread: GC.Spread.Sheets.Workbook
): void {
  registerSafeRowCommand(
    spread,
    'safeDeleteRows',
    (
      sheet: GC.Spread.Sheets.Worksheet,
      options: SafeAddRowsCommandOptions | SafeDeleteRowsCommandOptions
    ): void => {
      sheet.deleteRows(options.row, options.rowCount ?? 1);
    }
  );
}

function registerSafeInsertCellsDownCommand(
  spread: GC.Spread.Sheets.Workbook
): void {
  registerSafeCellShiftCommand(
    spread,
    'safeInsertCellsDown',
    'Designer.insertDownCells'
  );
}

function registerSafeDeleteCellsUpCommand(
  spread: GC.Spread.Sheets.Workbook
): void {
  registerSafeCellShiftCommand(
    spread,
    'safeDeleteCellsUp',
    'Designer.deleteUpCells'
  );
}

function registerSafeRowCommands(spread: GC.Spread.Sheets.Workbook): void {
  registerSafeAddRowsCommand(spread);
  registerSafeDeleteRowsCommand(spread);
  registerSafeInsertCellsDownCommand(spread);
  registerSafeDeleteCellsUpCommand(spread);
}

function isFullRowSelection(
  sheet: GC.Spread.Sheets.Worksheet,
  range: GC.Spread.Sheets.Range
): boolean {
  return range.col === -1 || range.colCount >= sheet.getColumnCount();
}

export function registerRowChangeHandler(
  spread: GC.Spread.Sheets.Workbook,
  confirm: ConfirmFn
) {
  // 関連するカスタムコマンドの登録
  registerSafeRowCommands(spread);

  // 行の挿入・削除、セルの上下方向への挿入・削除のハンドリング
  spread.bind(
    GC.Spread.Sheets.Events.RowChanging,
    async (
      _e: unknown,
      args: GC.Spread.Sheets.IRowChangedEventArgs & { cancel: boolean }
    ) => {
      const sheet = args.sheet;
      if (!sheet) return;

      const selection = sheet.getSelections()[0];
      if (!selection) return;

      const isInsert = args.propertyName === 'addRows';
      const isDelete = args.propertyName === 'deleteRows';

      if (!isInsert && !isDelete) return;

      // 行全体の挿入・削除
      if (isFullRowSelection(sheet, selection)) {
        const loops = collectUniqueLoops(
          findLoopsIntersectingRows(sheet, args.row, args.count ?? 1)
        );

        if (loops.length === 0) {
          return;
        }

        args.cancel = true;

        const ok = await confirm({
          message: isInsert
            ? translate('ADD_ROW_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE')
            : translate(
                'DELETE_ROW_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE'
              ),
        });
        spread.focus(true);

        if (!ok) {
          return;
        }

        spread.commandManager().execute({
          cmd: isInsert ? 'safeAddRows' : 'safeDeleteRows',
          sheetName: sheet.name(),
          row: args.row,
          rowCount: args.count ?? 1,
          loopIds: loops.map((loop) => loop.loopId),
        });

        return;
      }

      // セル上下方向への挿入・削除
      const affectedLoops = collectUniqueLoops(
        findLoopsAffectedByCellShift(sheet, selection, 'insertDown')
      );

      if (affectedLoops.length === 0) {
        return;
      }

      args.cancel = true;

      const ok = await confirm({
        message: isInsert
          ? translate('ADD_CELL_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE')
          : translate('DELETE_CELL_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE'),
      });
      spread.focus(true);

      if (!ok) {
        return;
      }

      spread.commandManager().execute({
        cmd: isInsert ? 'safeInsertCellsDown' : 'safeDeleteCellsUp',
        sheetName: sheet.name(),
        range: selection,
        loopIds: affectedLoops.map((loop) => loop.loopId),
      });
    }
  );

  const originalDeleteUpCells =
    // @ts-ignore
    spread.commandManager()['Designer.deleteUpCells'];

  // NOTE: セル削除はRowChangingで拾えないため、直接コマンドをカスタムする
  spread.commandManager().register('Designer.deleteUpCells', {
    canUndo: true,
    execute: async (
      context: GC.Spread.Sheets.Workbook,
      options: { selections: GC.Spread.Sheets.Range[]; sheetName: string },
      isUndo: boolean
    ): Promise<void> => {
      const Commands = GC.Spread.Sheets.Commands;

      if (isUndo) {
        Commands.undoTransaction(context, options);
        return;
      }

      const sheet = context.getSheetFromName(options.sheetName);
      if (!sheet) {
        return;
      }
      const selection = options.selections[0];

      // セルの上方向への削除
      const loops = collectUniqueLoops(
        findLoopsAffectedByCellShift(sheet, selection, 'deleteUp')
      );

      if (loops.length > 0) {
        const ok = await confirm({
          message: translate(
            'DELETE_CELL_OPERATION_WILL_CHANGE_LOOP_RANGE_STRUCTURE'
          ),
        });

        spread.focus();

        if (!ok) return;
      }

      Commands.startTransaction(context, options);

      try {
        for (const loopId of loops.map((loop) => loop.loopId)) {
          removeLoopById(sheet, loopId);
        }

        originalDeleteUpCells.execute(context, options, false);

        Commands.endTransaction(context, options);
        return;
      } catch (error) {
        Commands.undoTransaction(context, options);
        throw error;
      }
    },
  });
}
